import { useEffect, useRef, useState } from "react";
import { StrategyWizard } from "@/components/campaign-strategy/StrategyWizard";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, RefreshCw, Target, ArrowLeft, CheckCircle2, MapPin, Users, Building2, Globe2, Flag, Crown, Briefcase, UserCheck, Phone, FileText, Receipt, Presentation, Factory, PenLine, Check, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { ChipInput, splitChips } from "@/components/ui/chip-input";
import { GoalFieldsForm, goalFieldsMissing, serializeGoalFields, type GoalFieldValues } from "./goalFields";
import { Package, BadgeIndianRupee, EyeOff, ArrowRight } from "lucide-react";

type Market = "india" | "international" | "both";
const INDIA_REGIONS = ["India", "Mumbai", "Pune", "Bengaluru", "Delhi NCR", "Chennai", "Hyderabad", "Ahmedabad", "Kolkata"];
const INTL_REGIONS = ["UAE", "Saudi Arabia", "Qatar", "USA", "UK", "Singapore", "Germany", "Australia", "Canada"];
const regionSuggestions = (m: Market | "") => (m === "india" ? INDIA_REGIONS : m === "international" ? INTL_REGIONS : [...INDIA_REGIONS.slice(0, 4), ...INTL_REGIONS.slice(0, 4)]);
const COUNTRY_PICKS = ["India", "UAE", "Saudi Arabia", "USA", "UK", "Singapore", "Qatar", "Germany", "Australia", "Canada"];
const INDIA_WORDS = new Set(INDIA_REGIONS.map((x) => x.toLowerCase()));
export function marketFromCountries(list: string[]): Market | "" {
  if (!list.length) return "";
  const india = list.filter((c) => INDIA_WORDS.has(c.trim().toLowerCase())).length;
  return india === list.length ? "india" : india === 0 ? "international" : "both";
}

export interface CampaignSuggestion {
  title: string;
  campaign_purpose: string;
  product: string;
  buyer_industries: string[];
  buyer_titles: string[];
  region: string;
  company_size: string;
  why: string;
  key_facts: string[];
  market?: Market;
}

interface Props {
  companyProfileId: string;
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guestToken?: string;
  onCreated: (knowledgeId: string) => void;
  /** When set, edit this saved campaign profile instead of creating a new one. */
  editKnowledgeId?: string | null;
}

const COMPANY_SIZES = ["1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"];
type GoalKey = "sell" | "partners" | "agency" | "hiring" | "networking" | "event" | "job_search" | "investors" | "other";
const PRODUCT_GOALS: GoalKey[] = ["sell", "partners", "agency"];
type AskOpt = { v: string; icon: typeof Phone; linkLabel?: string; linkPlaceholder?: string };
type GoalCfg = { titlesLabel: string; titlesHelp: string; titlesPlaceholder: string; regionHelp: string; showSize: boolean; sizeLabel?: string; asks: AskOpt[] | null; card: { offer: string; who: string } };
const SALES_CFG: GoalCfg = { titlesLabel: "Job titles to target", titlesHelp: "The exact LinkedIn titles we'll search for in this campaign.", titlesPlaceholder: "e.g. Purchase Head", regionHelp: "Countries, states or cities where your buyers are.", showSize: true, sizeLabel: "Buyer company size", asks: null, card: { offer: "Sell", who: "Talk to" } };
const GOAL_CONFIG: Record<GoalKey, GoalCfg> = {
  sell: SALES_CFG, partners: { ...SALES_CFG, titlesHelp: "Titles of the partner / distributor decision makers.", sizeLabel: "Partner company size" }, agency: SALES_CFG,
  investors: { titlesLabel: "Investors to reach", titlesHelp: "Investor titles at VC / PE firms, angel networks and family offices.", titlesPlaceholder: "e.g. Partner, Angel Investor", regionHelp: "Where the investors are based.", showSize: false,
    asks: [{ v: "A short intro call", icon: Phone, linkLabel: "Meeting booking link", linkPlaceholder: "https://calendly.com/your-name" }, { v: "Share our pitch deck", icon: FileText, linkLabel: "Pitch deck link", linkPlaceholder: "https://docsend.com/..." }, { v: "Meet at an event", icon: Presentation }], card: { offer: "Raise", who: "Reach" } },
  hiring: { titlesLabel: "Candidate job titles", titlesHelp: "Titles that people in this role have today.", titlesPlaceholder: "e.g. React Developer", regionHelp: "Where the candidates should be.", showSize: false,
    asks: [{ v: "A short chat about the role", icon: Phone, linkLabel: "Meeting booking link", linkPlaceholder: "https://calendly.com/your-name" }, { v: "Share the job description", icon: FileText, linkLabel: "Job description link", linkPlaceholder: "https://..." }, { v: "Apply via link", icon: Globe2, linkLabel: "Application link", linkPlaceholder: "https://careers..." }], card: { offer: "Hire", who: "Reach" } },
  job_search: { titlesLabel: "Hiring managers and recruiters to reach", titlesHelp: "People who hire for the role you want.", titlesPlaceholder: "e.g. Head of Product, Talent Acquisition", regionHelp: "Where you want to work.", showSize: true, sizeLabel: "Company size you want to work at",
    asks: [{ v: "A referral", icon: UserCheck }, { v: "A quick chat", icon: Phone, linkLabel: "Meeting booking link", linkPlaceholder: "https://calendly.com/your-name" }, { v: "Review my CV", icon: FileText, linkLabel: "CV / portfolio link", linkPlaceholder: "https://..." }], card: { offer: "Find", who: "Reach" } },
  networking: { titlesLabel: "People you want to meet", titlesHelp: "Titles of the people you want in your network.", titlesPlaceholder: "e.g. CFO, Founder", regionHelp: "Where these people are.", showSize: false,
    asks: [{ v: "Connect and stay in touch", icon: UserPlus }, { v: "A virtual coffee", icon: Phone, linkLabel: "Meeting booking link", linkPlaceholder: "https://calendly.com/your-name" }], card: { offer: "Goal", who: "Meet" } },
  event: { titlesLabel: "People to invite", titlesHelp: "Titles of the people who should attend.", titlesPlaceholder: "e.g. Plant Head", regionHelp: "Where the attendees are.", showSize: false,
    asks: [{ v: "Register for the event", icon: Presentation, linkLabel: "Registration link", linkPlaceholder: "https://..." }, { v: "Meet us at the event / booth", icon: MapPin }], card: { offer: "Event", who: "Invite" } },
  other: { titlesLabel: "Who to reach", titlesHelp: "LinkedIn titles of the people to contact.", titlesPlaceholder: "e.g. Founder", regionHelp: "Where these people are.", showSize: false,
    asks: [{ v: "A short call", icon: Phone, linkLabel: "Meeting booking link", linkPlaceholder: "https://calendly.com/your-name" }], card: { offer: "Goal", who: "Reach" } },
};
const GOALS: { v: GoalKey; label: string; help: string; icon: typeof Flag; detailLabel?: string; detailPlaceholder?: string }[] = [
  { v: "sell", label: "Win new clients", help: "Sell your products or services", icon: Target },
  { v: "partners", label: "Find partners", help: "Resellers, dealers, channel partners", icon: Users },
  { v: "hiring", label: "Hire people", help: "Reach candidates for open roles", icon: UserPlus },
  { v: "networking", label: "Networking", help: "Grow your network with peers and leaders", icon: Globe2 },
  { v: "event", label: "Promote an event or webinar", help: "Invite before, follow up after", icon: Presentation },
  { v: "job_search", label: "Job search", help: "Reach hiring managers and recruiters", icon: Briefcase },
  { v: "investors", label: "Raise funding", help: "Reach investors and VCs", icon: Crown },
  { v: "other", label: "Something else", help: "Describe your own goal", icon: PenLine },
];
const FIRST_ASKS: { v: string; icon: typeof Phone }[] = [
  { v: "A short call", icon: Phone }, { v: "Send our catalogue / brochure", icon: FileText }, { v: "Share a quote", icon: Receipt },
  { v: "Book a demo", icon: Presentation }, { v: "Visit our plant / office", icon: Factory },
];
const MARKETS: { v: Market; label: string; help: string; icon: typeof Flag }[] = [
  { v: "india", label: "India", help: "Buyers in Indian cities and states", icon: Flag },
  { v: "international", label: "International", help: "Buyers outside India", icon: Globe2 },
  { v: "both", label: "Both", help: "India and abroad", icon: MapPin },
];
const LEVELS: { v: string; label: string; help: string; icon: typeof Crown }[] = [
  { v: "owner", label: "Top decision-maker", help: "Owner, Founder, CEO / CXO", icon: Crown },
  { v: "department_head", label: "Department head", help: "Director, Head, VP of the team", icon: Briefcase },
  { v: "both", label: "Either", help: "Both levels — wider reach", icon: UserCheck },
];
/** Goals that need one quick link before we can suggest campaigns. */
const SOURCE_INPUTS: Partial<Record<GoalKey, { label: string; help: string; placeholder: string }>> = {
  hiring: { label: "Paste the job link", help: "We'll read the role from it. Optional.", placeholder: "https://careers.yourcompany.com/job/..." },
  event: { label: "Paste the event link", help: "We'll read the event name, date and audience. Optional.", placeholder: "https://..." },
  investors: { label: "Paste your pitch deck link", help: "A shareable link (Google Drive, DocSend, website). Optional.", placeholder: "https://docsend.com/..." },
};

async function call(body: Record<string, unknown>) {
  const { data, error } = await supabase.functions.invoke("company-profile-builder", { body });
  if (error) {
    let msg = error.message;
    try { const ctx = await (error as any).context?.json?.(); if (ctx?.error) msg = ctx.error; } catch { /* ignore */ }
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

function OptionTile({ active, onClick, icon: Icon, label, help }: { active: boolean; onClick: () => void; icon: typeof Flag; label: string; help?: string }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}
      className={cn("group relative flex w-full items-start gap-3 rounded-xl border-2 p-3 text-left transition-all",
        active ? "border-sage bg-sage-muted shadow-sm" : "border-border bg-card hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-sm")}>
      <span className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2",
        active ? "border-sage bg-sage text-sage-foreground" : "border-muted-foreground/40 group-hover:border-primary")}>
        {active && <Check className="h-3 w-3" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-sm font-medium"><Icon className={cn("h-4 w-4", active ? "text-sage" : "text-muted-foreground")} />{label}</span>
        {help && <span className="mt-0.5 block text-xs text-muted-foreground">{help}</span>}
      </span>
    </button>
  );
}

function Section({ n, title, help, done, children }: { n: number; title: string; help?: string; done: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-start gap-2.5">
        <span className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold", done ? "bg-sage text-sage-foreground" : "bg-primary/10 text-primary")}>
          {done ? <Check className="h-3.5 w-3.5" /> : n}
        </span>
        <div>
          <Label className="text-sm font-semibold">{title}</Label>
          {help && <p className="text-xs text-muted-foreground">{help}</p>}
        </div>
      </div>
      <div className="pl-8">{children}</div>
    </div>
  );
}

function LegacyCampaignProfileBuilderDialog({ companyProfileId, workspaceId, open, onOpenChange, guestToken, onCreated, editKnowledgeId }: Props) {
  const base = { workspaceId: guestToken ? null : workspaceId, guestToken: guestToken ?? null, profileId: companyProfileId };
  const [suggestions, setSuggestions] = useState<CampaignSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState<string[]>([]);
  const [askCountries, setAskCountries] = useState(true);
  const [picked, setPicked] = useState<CampaignSuggestion | null>(null);
  const [title, setTitle] = useState("");
  const [market, setMarket] = useState<Market | "">("");
  const [titles, setTitles] = useState<string[]>([]);
  const [companySizes, setCompanySizes] = useState<string[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [industriesFromProfile, setIndustriesFromProfile] = useState(false);
  const [asked, setAsked] = useState(false);
  const [contactLevel, setContactLevel] = useState("");
  const [region, setRegion] = useState("");
  const [firstAsk, setFirstAsk] = useState("");
  const [customAsk, setCustomAsk] = useState("");
  const [tone, setTone] = useState("");
  const [reach, setReach] = useState<"new" | "existing">("new");
  const [askDetails, setAskDetails] = useState<{ link: string; duration: string; location: string; quote_info: string; phone: string }>({ link: "", duration: "", location: "", quote_info: "", phone: "" });
  const setDetail = (k: keyof typeof askDetails, v: string) => setAskDetails((d) => ({ ...d, [k]: v }));
  const [allProducts, setAllProducts] = useState<{ name: string; description?: string; ticket_size?: string }[]>([]);
  const [chosen, setChosen] = useState<string[]>([]);
  const [pricing, setPricing] = useState<{ disclose: boolean | null; mode: string; text: string }>({ disclose: null, mode: "", text: "" });
  const [saving, setSaving] = useState(false);
  const isEdit = !!editKnowledgeId;
  const [goal, setGoal] = useState<GoalKey | "">("");
  const [customGoal, setCustomGoal] = useState("");
  const [setupStep, setSetupStep] = useState(0);
  const [goalFields, setGoalFields] = useState<GoalFieldValues>({});
  const goalDetails = serializeGoalFields(goal || "sell", goalFields);
  const goalReqMissing = goal ? goalFieldsMissing(goal, goalFields) : [];
  const goalMeta = GOALS.find((g) => g.v === goal);
  const isProductGoal = !goal || PRODUCT_GOALS.includes(goal as GoalKey);
  const cfg = GOAL_CONFIG[(goal || "sell") as GoalKey];
  const goalAsks = cfg.asks;
  const askOpt = goalAsks?.find((a) => a.v === firstAsk);
  const nSize = cfg.showSize ? 1 : 0;
  const [sourceUrl, setSourceUrl] = useState("");
  const [refineText, setRefineText] = useState("");
  const [refining, setRefining] = useState(false);
  const [preview, setPreview] = useState("");
  const [previewing, setPreviewing] = useState(false);
  const [profileReady, setProfileReady] = useState(false);
  const autoPreviewFor = useRef<string>("");
  const validSource = /^https?:\/\/\S+$/i.test(sourceUrl.trim()) ? sourceUrl.trim() : "";
  const sourceCfg = goal ? SOURCE_INPUTS[goal as GoalKey] : undefined;

  const load = async (refresh = false, list: string[] = countries) => {
    if (!list.length) { setAskCountries(true); return; }
    setAskCountries(false);
    setAsked(true);
    setLoading(true);
    try {
      const url = /^https?:\/\//i.test(sourceUrl.trim()) ? sourceUrl.trim() : undefined;
      const res = await call({ ...base, action: "suggest_campaigns", refresh, countries: list, products: isProductGoal ? chosen.slice(0, 1) : [], one_product_per_campaign: true, industries, company_sizes: cfg.showSize ? companySizes : [], market: marketFromCountries(list) || "both", goal: goal || "sell", goalCustom: goal === "other" ? customGoal.trim() : "", goalDetails: goalDetails.trim(), sourceUrl: url });
      setSuggestions(res.suggestions ?? []);
      setTimeout(() => document.getElementById("cpb-ready")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    } catch (e: any) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    setPicked(null);
    call({ ...base, action: "get" }).then((p) => {
      const ps = (p?.extracted_facts?.products ?? []).filter((x: any) => String(x?.name ?? "").trim());
      setAllProducts(ps);
      if (!editKnowledgeId) {
        setChosen([]);
        const f = p?.extracted_facts ?? {};
        const fromProfile = [...(Array.isArray(f.countries) ? f.countries : []), ...(Array.isArray(f.regions) ? f.regions : [])]
          .map((x: any) => String(typeof x === "string" ? x : x?.name ?? "").trim()).filter((x: string) => COUNTRY_PICKS.some((c) => c.toLowerCase() === x.toLowerCase()));
        setCountries((cur) => (cur.length ? cur : fromProfile.length ? Array.from(new Set(fromProfile)).slice(0, 5) : ["India"]));
        setProfileReady(true);
        const ind = (Array.isArray(f.industries_served) ? f.industries_served : []).map((x: any) => String(x).trim()).filter(Boolean).slice(0, 6);
        setIndustries(ind); setIndustriesFromProfile(ind.length > 0); setCompanySizes([]); setAsked(false);
        const la = p?.last_ask;
        if (la?.first_ask) {
          setFirstAsk((cur) => {
            if (cur) return cur;
            const known = FIRST_ASKS.some((a) => a.v === la.first_ask) || Object.values(GOAL_CONFIG).some((g) => g.asks?.some((a) => a.v === la.first_ask));
            if (!known) setCustomAsk(la.first_ask);
            return known ? la.first_ask : "custom";
          });
          const d = la.ask_details ?? {};
          setAskDetails((cur) => (cur.link || cur.phone ? cur : { link: d.link ?? "", duration: d.duration ?? "", location: d.location ?? "", quote_info: d.quote_info ?? "", phone: d.phone ?? "" }));
        }
      }
    }).catch(() => { /* products are optional context */ });
    if (editKnowledgeId) {
      setLoading(true);
      call({ ...base, action: "get_campaign_profile", campaignKnowledgeId: editKnowledgeId })
        .then((res) => {
          const v2 = res.v2 ?? {};
          const s = v2.suggestion;
          if (!s) { toast.error("This campaign profile can't be edited here"); onOpenChange(false); return; }
          const c = v2.confirmed ?? {};
          setPicked(s);
          setTitle(res.title ?? s.title);
          setMarket((c.market || v2.market || s.market || "") as Market | "");
          setTitles(Array.isArray(v2.buyer_titles) && v2.buyer_titles.length ? v2.buyer_titles : s.buyer_titles ?? []);
          setCompanySizes(Array.isArray(c.company_sizes) ? c.company_sizes : c.company_size ? String(c.company_size).split(/,\s*/).filter(Boolean) : []);
          setContactLevel(c.contact_level ?? "");
          setRegion(c.region ?? s.region ?? "");
          setCountries(Array.isArray(c.countries) ? c.countries : []);
          if (c.first_ask && !FIRST_ASKS.some((a) => a.v === c.first_ask) && !Object.values(GOAL_CONFIG).some((g) => g.asks?.some((a) => a.v === c.first_ask))) { setFirstAsk("custom"); setCustomAsk(c.first_ask); }
          else { setFirstAsk(c.first_ask ?? ""); setCustomAsk(""); }
          setTone(c.tone || "");
          setReach(c.reach === "existing" ? "existing" : "new");
          const d = c.ask_details ?? {};
          setChosen(Array.isArray(c.products) && c.products.length ? c.products : s.product ? [s.product] : []);
          setGoal(c.goal === "agency" ? "sell" : GOALS.some((g) => g.v === c.goal) ? c.goal : "sell");
          if (c.goal_fields && typeof c.goal_fields === "object") setGoalFields(c.goal_fields);
          setCustomGoal(c.goal_custom ?? "");
          setPricing(c.pricing ? { disclose: !!c.pricing.disclose, mode: c.pricing.mode ?? "", text: c.pricing.text ?? "" } : { disclose: null, mode: "", text: "" });
          setAskDetails({ link: d.link ?? "", duration: d.duration ?? "", location: d.location ?? "", quote_info: d.quote_info ?? "", phone: d.phone ?? "" });
        })
        .catch((e) => { toast.error(e.message); onOpenChange(false); })
        .finally(() => setLoading(false));
    } else {
      setSuggestions([]); setAskCountries(true); setSetupStep(0); setPreview(""); setRefineText(""); setProfileReady(false); setCountries([]); autoPreviewFor.current = "";
      setGoal((g) => g || "sell");
      setReach("new");
      setPricing((p) => (p.disclose === null ? { disclose: false, mode: "", text: "" } : p));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, companyProfileId, editKnowledgeId]);

  const pick = (s: CampaignSuggestion) => {
    setPicked(s);
    setTitle(s.title);
    setMarket(marketFromCountries(countries) || (s.market ?? ""));
    setTitles(s.buyer_titles ?? []);
    setRegion(countries.length ? countries.join(", ") : s.region || "");
    setContactLevel("");
    setPreview("");
  };

  const ask = firstAsk === "custom" ? customAsk.trim() : firstAsk;
  const regionList = splitChips(region);
  const effectiveMarket = marketFromCountries(regionList) || market || "both";
  const missing = [!regionList.length && "countries / region", !titles.length && "job titles", !ask && "call to action", isProductGoal && pricing.disclose === null && "pricing choice", isProductGoal && pricing.disclose === true && !pricing.text.trim() && "price to share"].filter(Boolean) as string[];

  const goalBody = () => ({ goal: goal || "sell", goalCustom: goal === "other" ? customGoal.trim() : "", goalDetails: goalDetails.trim() });

  const refine = async () => {
    if (!picked || !refineText.trim()) return;
    setRefining(true);
    try {
      const res = await call({ ...base, ...goalBody(), action: "refine_suggestion", instruction: refineText.trim(), suggestion: { ...picked, region } });
      const s = res.suggestion as CampaignSuggestion;
      setPicked(s); setTitle(s.title); setTitles(s.buyer_titles ?? []);
      if (cfg.showSize && COMPANY_SIZES.includes(s.company_size)) setCompanySizes([s.company_size]);
      setRefineText(""); setPreview("");
      toast.success("Campaign updated");
    } catch (e: any) { toast.error(e.message); }
    finally { setRefining(false); }
  };

  const loadPreview = async () => {
    if (!picked) return;
    setPreviewing(true);
    try {
      const res = await call({ ...base, ...goalBody(), action: "preview_first_message", suggestion: { ...picked, region }, confirm: { buyer_titles: titles, reach, first_ask: ask, ask_details: askDetails } });
      setPreview(res.message ?? "");
    } catch (e: any) { toast.error(e.message); }
    finally { setPreviewing(false); }
  };

  // Show the first message by itself once a campaign is picked and a call to action is known.
  useEffect(() => {
    if (isEdit || !picked || !ask || previewing) return;
    const key = `${picked.title}|${picked.product}`;
    if (autoPreviewFor.current === key) return;
    autoPreviewFor.current = key;
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, ask]);

  const create = async () => {
    if (missing.length) { toast.error(`Please choose: ${missing.join(", ")}`); return; }
    setSaving(true);
    try {
      const res = await call({
        ...base, action: isEdit ? "update_campaign_profile" : "create_campaign_profile", campaignKnowledgeId: editKnowledgeId ?? undefined,
        suggestion: picked, title,
        confirm: { goal: goal || "sell", goal_custom: goal === "other" ? customGoal.trim() : "", goal_details: [goalDetails.trim(), sourceUrl.trim() && `Link: ${sourceUrl.trim()}`].filter(Boolean).join("\n"), goal_fields: goalFields, company_size: cfg.showSize ? companySizes.join(", ") : "", company_sizes: cfg.showSize ? companySizes : [], contact_level: contactLevel || undefined, region, first_ask: ask, tone: tone || undefined, reach, ask_details: askDetails, market: effectiveMarket, countries: regionList, buyer_titles: titles, products: isProductGoal ? (isEdit && chosen.length ? chosen : [picked?.product].filter(Boolean)) : [], pricing: isProductGoal ? { disclose: pricing.disclose === true, mode: pricing.mode || undefined, text: pricing.text.trim() } : { disclose: false, text: "" } },
      });
      toast.success(isEdit ? "Campaign profile updated" : "Campaign profile created");
      onCreated(res.id);
      onOpenChange(false);
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const Tag = ({ icon: Icon, children }: { icon: typeof Target; children: React.ReactNode }) => (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground"><Icon className="h-3 w-3 text-muted-foreground" />{children}</span>
  );

  const composeMissing = [!goal && "goal", goal === "other" && !customGoal.trim() && "goal description", isProductGoal && allProducts.length > 0 && !chosen.length && "product", !countries.length && "countries", !ask && "call to action"].filter(Boolean) as string[];
  const ctaSection = (
              <Section n={2} title="What should be the call to action?" help="The one action your messages will ask people to take." done={!!ask}>
                <div className="grid gap-2 sm:grid-cols-2">
                  {(goalAsks ?? FIRST_ASKS).map((a) => <OptionTile key={a.v} active={firstAsk === a.v} onClick={() => setFirstAsk(a.v)} icon={a.icon} label={a.v} />)}
                  <OptionTile active={firstAsk === "custom"} onClick={() => setFirstAsk("custom")} icon={PenLine} label="Something else…" />
                </div>
                {firstAsk === "custom" && <Input autoFocus className={cn("mt-2", !customAsk.trim() && "border-warning")} value={customAsk} onChange={(e) => setCustomAsk(e.target.value)} placeholder="Type your call to action (required)" />}
                {askOpt?.linkLabel && (
                  <div className="mt-3 space-y-1 rounded-lg border border-dashed bg-muted/30 p-3">
                    <Label className="text-xs">{askOpt.linkLabel} (optional)</Label>
                    <Input value={askDetails.link} onChange={(e) => setDetail("link", e.target.value)} placeholder={askOpt.linkPlaceholder} />
                  </div>
                )}
                {!goalAsks && firstAsk && firstAsk !== "custom" && (
                  <div className="mt-3 grid gap-2 rounded-lg border border-dashed bg-muted/30 p-3 sm:grid-cols-2">
                    {(firstAsk === "A short call" || firstAsk === "Book a demo" || firstAsk === "Visit our plant / office") && (
                      <div className="space-y-1 sm:col-span-2">
                        <Label className="text-xs">{firstAsk === "Book a demo" ? "Demo booking link" : firstAsk === "A short call" ? "Meeting booking link (e.g. Calendly)" : "Visit booking link"} (optional)</Label>
                        <Input value={askDetails.link} onChange={(e) => setDetail("link", e.target.value)} placeholder="https://calendly.com/your-name" />
                      </div>
                    )}
                    {firstAsk === "Send our catalogue / brochure" && (
                      <div className="space-y-1 sm:col-span-2">
                        <Label className="text-xs">Catalogue / brochure link (optional)</Label>
                        <Input value={askDetails.link} onChange={(e) => setDetail("link", e.target.value)} placeholder="https://yourcompany.com/catalogue.pdf" />
                      </div>
                    )}
                    {firstAsk === "A short call" && (
                      <div className="space-y-1 sm:col-span-2">
                        <Label className="text-xs">Phone / WhatsApp number (optional)</Label>
                        <Input type="tel" value={askDetails.phone} onChange={(e) => setDetail("phone", e.target.value)} placeholder="e.g. +91 98765 43210" />
                      </div>
                    )}
                    {firstAsk === "A short call" && (
                      <div className="space-y-1 sm:col-span-2">
                        <Label className="text-xs">Call length (optional)</Label>
                        <div className="flex flex-wrap gap-2">
                          {["15 min", "20 min", "30 min"].map((d) => (
                            <button key={d} type="button" onClick={() => setDetail("duration", askDetails.duration === d ? "" : d)}
                              className={cn("rounded-full border-2 px-3 py-1 text-sm", askDetails.duration === d ? "border-sage bg-sage text-sage-foreground" : "hover:border-primary/40")}>{d}</button>
                          ))}
                        </div>
                        <Input
                          className="mt-1"
                          value={askDetails.duration}
                          onChange={(e) => setDetail("duration", e.target.value)}
                          placeholder="Or type your own — e.g. 45 min"
                        />
                      </div>
                    )}
                    {firstAsk === "Visit our plant / office" && (
                      <div className="space-y-1 sm:col-span-2">
                        <Label className="text-xs">Location — city / area (optional)</Label>
                        <Input value={askDetails.location} onChange={(e) => setDetail("location", e.target.value)} placeholder="e.g. MIDC Bhosari, Pune" />
                      </div>
                    )}
                    {firstAsk === "Share a quote" && (
                      <div className="space-y-1 sm:col-span-2">
                        <Label className="text-xs">What details should they send for the quote? (optional)</Label>
                        <Input value={askDetails.quote_info} onChange={(e) => setDetail("quote_info", e.target.value)} placeholder="e.g. quantity and specs" />
                      </div>
                    )}
                  </div>
                )}
              </Section>
  );
  const sizeSection = cfg.showSize ? (
<Section n={3} title={cfg.sizeLabel ?? "Company size"} help="Pick one or more employee ranges (optional)." done={companySizes.length > 0}>
                <div className="flex flex-wrap gap-2">
                  {COMPANY_SIZES.map((sz) => {
                    const on = companySizes.includes(sz);
                    return (
                      <button key={sz} type="button" aria-pressed={on} onClick={() => setCompanySizes(on ? companySizes.filter((x) => x !== sz) : [...companySizes, sz])}
                        className={cn("inline-flex items-center gap-1 rounded-full border-2 px-3 py-1 text-sm transition-colors", on ? "border-sage bg-sage text-sage-foreground" : "hover:border-primary/40")}>
                        {on && <Check className="h-3.5 w-3.5" />}{sz}
                      </button>
                    );
                  })}
                </div>
              </Section>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col gap-0 overflow-hidden p-0">
        <div className="bg-gradient-to-br from-primary/15 via-primary/5 to-background px-6 pt-6 pb-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
              {picked ? <Target className="h-5 w-5" /> : <Sparkles className="h-5 w-5" />}
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-xl font-semibold">{isEdit ? "Edit campaign" : picked ? "Almost done" : "New campaign"}</DialogTitle>
              <DialogDescription className="mt-1">
                {isEdit ? "Change anything, then save." : picked ? "Check it, tweak if needed, and save." : "Answer a few quick questions — most are filled in from your business profile."}
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && isEdit ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-7 w-7 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading your campaign…</p>
            </div>
          ) : !picked ? (
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">What should this campaign do?</Label>
                <div className="flex flex-wrap gap-1.5">
                  {GOALS.map((g) => {
                    const on = goal === g.v; const Icon = g.icon;
                    return (
                      <button key={g.v} type="button" aria-pressed={on} onClick={() => { if (!on) { setGoal(g.v); setGoalFields({}); setSourceUrl(""); setFirstAsk(""); setSuggestions([]); setAsked(false); } }}
                        className={cn("inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-sm transition-all", on ? "border-sage bg-sage text-sage-foreground shadow-sm" : "bg-card hover:-translate-y-0.5 hover:border-primary/40")}>
                        {on ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5 text-muted-foreground" />}{g.label}
                      </button>
                    );
                  })}
                </div>
                {goal === "other" && <Input value={customGoal} onChange={(e) => setCustomGoal(e.target.value)} placeholder="Describe the goal in a few words" className="animate-in fade-in" />}
                {sourceCfg && (
                  <div className="animate-in fade-in slide-in-from-top-1 space-y-1">
                    <Input value={sourceUrl} onChange={(e) => setSourceUrl(e.target.value)} placeholder={`${sourceCfg.label} (optional) — ${sourceCfg.placeholder}`} />
                    {sourceUrl.trim() && !/^https?:\/\//i.test(sourceUrl.trim()) && <p className="text-xs text-warning">Paste a full link starting with https://</p>}
                  </div>
                )}
              </div>

              {isProductGoal && allProducts.length > 0 && (
                <div className="animate-in fade-in space-y-2">
                  <Label className="text-sm font-semibold">Which product is this campaign for?</Label>
                  <p className="text-xs text-muted-foreground">One campaign = one product, so messages stay focused.</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {allProducts.map((p) => <OptionTile key={p.name} active={chosen[0] === p.name} onClick={() => setChosen([p.name])} icon={Package} label={p.name} help={p.description ? String(p.description).slice(0, 80) : undefined} />)}
                  </div>
                </div>
              )}
              {!isProductGoal && goal && goal !== "other" && (
                <div className="animate-in fade-in space-y-2 rounded-xl border bg-muted/20 p-3">
                  <Label className="text-sm font-semibold">A few details</Label>
                  <GoalFieldsForm goal={goal} values={goalFields} onChange={setGoalFields} />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm font-semibold">Where?</Label>
                <div className="flex flex-wrap items-center gap-1.5">
                  {MARKETS.map((m) => {
                    const on = marketFromCountries(countries) === m.v;
                    const intl = countries.filter((c) => !INDIA_WORDS.has(c.toLowerCase()));
                    return (
                      <button key={m.v} type="button" aria-pressed={on}
                        onClick={() => setCountries(m.v === "india" ? ["India"] : m.v === "international" ? (intl.length ? intl : ["UAE"]) : ["India", ...(intl.length ? intl : ["UAE"])])}
                        className={cn("inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-sm transition-all", on ? "border-sage bg-sage text-sage-foreground shadow-sm" : "bg-card hover:border-primary/40")}>
                        {on ? <Check className="h-3.5 w-3.5" /> : <m.icon className="h-3.5 w-3.5 text-muted-foreground" />}{m.label}
                      </button>
                    );
                  })}
                </div>
                <ChipInput value={countries} onChange={setCountries} placeholder="Add a country, e.g. USA" suggestions={COUNTRY_PICKS} maxSuggestions={10} aria-label="Countries" />
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold">{cfg.showSize ? "Industries to target" : "Industries (optional)"}{industriesFromProfile && <span className="ml-2 rounded-full bg-sage-muted px-2 py-0.5 text-[10px] font-medium text-sage">From your business profile</span>}</Label>
                <ChipInput value={industries} onChange={(v) => { setIndustries(v); setIndustriesFromProfile(false); }} placeholder="e.g. Pharmaceuticals, Automotive" aria-label="Industries" />
              </div>

              {sizeSection}

              {ctaSection}

              <div className="sticky -bottom-5 z-10 -mx-6 space-y-1 border-t bg-background px-6 pt-3 pb-4">
                <Button className="w-full gap-2" size="lg" disabled={composeMissing.length > 0 || loading} onClick={() => load(asked)}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {asked ? "Suggest again" : "Suggest campaigns"}
                </Button>
                {composeMissing.length > 0 && <p className="text-center text-xs text-warning">Still needed: {composeMissing.join(", ")}</p>}
              </div>

              {asked && <div id="cpb-ready" className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1.5 text-sm font-semibold"><Sparkles className="h-4 w-4 text-primary" /> Ready for you</Label>
                  {!loading && suggestions.length > 0 && <Button variant="ghost" size="sm" onClick={() => load(true)} className="h-7 gap-1.5 text-xs"><RefreshCw className="h-3.5 w-3.5" /> Show others</Button>}
                </div>
                {loading ? (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">Reading your business and building campaigns…</p>
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="animate-pulse space-y-2 rounded-xl border-2 p-4" style={{ animationDelay: `${i * 150}ms` }}>
                        <div className="h-4 w-2/3 rounded bg-muted" /><div className="h-3 w-1/2 rounded bg-muted" /><div className="h-3 w-5/6 rounded bg-muted" />
                      </div>
                    ))}
                  </div>
                ) : !countries.length ? (
                  <p className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">Pick where to find people and campaigns will appear here.</p>
                ) : suggestions.map((s, i) => (
                  <div key={`${s.title}-${i}`} className="group animate-in fade-in slide-in-from-bottom-2 fill-mode-both rounded-xl border-2 bg-card p-4 transition-all hover:border-primary/60 hover:shadow-md" style={{ animationDelay: `${i * 120}ms` }}>
                    <div className="flex items-start gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Package className="h-4 w-4" /></div>
                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="font-semibold leading-snug">{s.product}</div>
                        <p className="text-sm text-muted-foreground">
                          <Users className="mr-1 inline h-3.5 w-3.5" />{s.buyer_titles?.slice(0, 2).join(", ")}{s.buyer_industries?.length ? ` at ${s.buyer_industries.slice(0, 2).join(", ")}` : ""}
                          <span className="mx-1.5">·</span><MapPin className="mr-1 inline h-3.5 w-3.5 text-sage" />{s.region}
                        </p>
                        {s.why && <p className="text-xs text-foreground/80"><span className="font-medium text-sage">Why: </span>{s.why}</p>}
                      </div>
                      <Button size="sm" onClick={() => pick(s)} className="shrink-0 gap-1">Use this <ArrowRight className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
                {!loading && countries.length > 0 && suggestions.length === 0 && (
                  <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
                    No campaigns yet. <button type="button" onClick={() => load(true)} className="font-medium text-primary hover:underline">Try again</button>
                  </div>
                )}
              </div>}
            </div>
          ) : (
            <div className="animate-in fade-in slide-in-from-bottom-2 space-y-6">
              {!isEdit && (
                <p className="text-lg leading-relaxed">
                  We'll {isProductGoal ? "offer" : cfg.card.offer.toLowerCase()} <span className="font-semibold text-primary">{picked.product}</span> to{" "}
                  <span className="font-semibold">{titles.slice(0, 2).join(", ") || "the right people"}</span>
                  {picked.buyer_industries?.length ? <> at <span className="font-semibold">{picked.buyer_industries.slice(0, 2).join(", ")}</span></> : null}
                  {" "}in <span className="font-semibold text-sage">{regionList.join(", ")}</span>.
                </p>
              )}
              <div className="space-y-2 rounded-xl border border-primary/20 bg-primary/5 p-4">
                <div className="flex flex-wrap items-center gap-2 text-sm font-semibold"><Target className="h-4 w-4 text-primary" /> {picked.product}
                  {goalMeta && <span className="rounded-full bg-background px-2 py-0.5 text-xs font-medium text-primary">{goalMeta.label}</span>}</div>
                {isEdit && <div className="space-y-1.5">
                  <Label className="text-xs">Campaign goal</Label>
                  <div className="flex flex-wrap gap-1.5">{GOALS.map((g) => (
                    <button key={g.v} type="button" onClick={() => { if (g.v === goal) return; setGoal(g.v); setGoalFields({}); setFirstAsk(""); setAskDetails({ link: "", duration: "", location: "", quote_info: "", phone: "" }); }}
                      className={cn("rounded-full border-2 px-2.5 py-0.5 text-xs", goal === g.v ? "border-sage bg-sage text-sage-foreground" : "bg-background hover:border-primary/40")}>{g.label}</button>
                  ))}</div>
                  {goal === "other" && <Input value={customGoal} onChange={(e) => setCustomGoal(e.target.value)} placeholder="Describe the goal" />}
                  {goal && <GoalFieldsForm goal={goal} values={goalFields} onChange={setGoalFields} />}
                </div>}
                <div className="flex flex-wrap gap-1.5">{picked.buyer_industries.map((t) => <Tag key={t} icon={Building2}>{t}</Tag>)}</div>
                {picked.key_facts.length > 0 && <ul className="space-y-0.5 pt-1 text-sm">{picked.key_facts.map((f) => <li key={f} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sage" />{f}</li>)}</ul>}
              </div>

              <div className="space-y-1.5">
                <Label>Campaign name</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              {!isEdit && (
                <div className="space-y-2 rounded-xl border bg-card p-3">
                  <Label className="text-sm font-semibold">Something off? Adjust it</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {regionList.length > 1 && regionList.slice(0, 4).map((r) => (
                      <button key={r} type="button" onClick={() => setRegion(r)} className="rounded-full border-2 px-2.5 py-0.5 text-xs hover:border-primary/40">Only {r}</button>
                    ))}
                    {isProductGoal && <button type="button" onClick={() => { setContactLevel("owner"); toast.success("We'll talk to owners and founders"); }} className={cn("rounded-full border-2 px-2.5 py-0.5 text-xs", contactLevel === "owner" ? "border-sage bg-sage text-sage-foreground" : "hover:border-primary/40")}>Talk to owners</button>}
                    {cfg.showSize && <button type="button" onClick={() => setCompanySizes(["201-500", "501-1000", "1000+"])} className="rounded-full border-2 px-2.5 py-0.5 text-xs hover:border-primary/40">Bigger companies</button>}
                    {cfg.showSize && <button type="button" onClick={() => setCompanySizes(["1-10", "11-50", "51-200"])} className="rounded-full border-2 px-2.5 py-0.5 text-xs hover:border-primary/40">Smaller companies</button>}
                  </div>
                  <div className="flex gap-2">
                    <Input value={refineText} onChange={(e) => setRefineText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") refine(); }} placeholder='Or type one line, e.g. "target hospitals instead"' />
                    <Button variant="outline" onClick={refine} disabled={refining || !refineText.trim()} className="shrink-0 gap-1.5">{refining ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Update</Button>
                  </div>
                </div>
              )}
              {(<>
              <details className="rounded-lg border p-3" open={isEdit}>
                <summary className="cursor-pointer text-sm font-medium">Who to reach <span className="font-normal text-muted-foreground">— {reach === "new" ? "new people" : "existing connections"}</span></summary>
                <div className="mt-3">
              <Section n={1} title="Who do you want to reach?" help="New people get a connection request first; existing connections are messaged directly." done>
                <div className="grid gap-2 sm:grid-cols-2">
                  <OptionTile active={reach === "new"} onClick={() => setReach("new")} icon={UserPlus} label="New people" help="Not connected yet" />
                  <OptionTile active={reach === "existing"} onClick={() => setReach("existing")} icon={Users} label="My existing connections" help="Already in your LinkedIn network" />
                </div>
              </Section>
                </div>
              </details>

              {isEdit && ctaSection}

              {!isEdit && (
                <div className="space-y-2 rounded-xl border border-primary/20 bg-primary/5 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-sm font-semibold">First message preview</Label>
                    <Button size="sm" variant="outline" onClick={loadPreview} disabled={previewing || !ask} className="gap-1.5 bg-background">
                      {previewing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {preview ? "Write again" : "Show preview"}
                    </Button>
                  </div>
                  {preview ? <p className="whitespace-pre-wrap rounded-lg bg-background p-3 text-sm">{preview}</p>
                    : <p className="text-xs text-muted-foreground">{ask ? "See how the first message will read. Full messages are written in the next step." : "Pick a call to action first."}</p>}
                </div>
              )}

              {isProductGoal && <details className="rounded-lg border p-3" open={isEdit || pricing.disclose === true}><summary className="cursor-pointer text-sm font-medium">Pricing in messages <span className="font-normal text-muted-foreground">— {pricing.disclose ? "shared" : "kept private"}</span></summary><div className="mt-3">
              <Section n={3} title="Do you want to mention pricing in your messages?" done={pricing.disclose === false || (pricing.disclose === true && !!pricing.text.trim())}>
                <div className="grid gap-2 sm:grid-cols-2">
                  <OptionTile active={pricing.disclose === true} onClick={() => setPricing((p) => ({ ...p, disclose: true }))} icon={BadgeIndianRupee} label="Yes, share pricing" />
                  <OptionTile active={pricing.disclose === false} onClick={() => setPricing({ disclose: false, mode: "", text: "" })} icon={EyeOff} label="No, keep pricing private" help="Messages will offer to discuss it on the call" />
                </div>
                {pricing.disclose === true && (
                  <div className="mt-3 space-y-2 rounded-lg border border-dashed bg-muted/30 p-3">
                    <Label className="text-xs">How much pricing do you want to share?</Label>
                    <div className="flex flex-wrap gap-2">
                      {[["starting", "Starting price only"], ["range", "Price range"], ["exact", "Exact price"], ["custom", "Something else"]].map(([v, l]) => (
                        <button key={v} type="button" onClick={() => setPricing((p) => ({ ...p, mode: v }))}
                          className={cn("rounded-full border-2 px-3 py-1 text-sm", pricing.mode === v ? "border-sage bg-sage text-sage-foreground" : "hover:border-primary/40")}>{l}</button>
                      ))}
                    </div>
                    <Input className={cn(!pricing.text.trim() && "border-warning")} value={pricing.text} onChange={(e) => setPricing((p) => ({ ...p, text: e.target.value }))}
                      placeholder={pricing.mode === "range" ? "e.g. ₹2 – 5 lakh per project" : pricing.mode === "exact" ? "e.g. ₹45,000 per unit" : "e.g. starting at ₹25,000"} />
                  </div>
                )}
              </Section></div></details>}
              </>)}

              <Section n={1} title="Countries / regions to target" help={cfg.regionHelp} done={regionList.length > 0}>
                <ChipInput value={regionList} onChange={(v) => setRegion(v.join(", "))} placeholder="e.g. UAE" suggestions={regionSuggestions(effectiveMarket)} maxSuggestions={6} aria-label="Region" />
              </Section>

              <Section n={2} title={cfg.titlesLabel} help={cfg.titlesHelp} done={titles.length > 0}>
                <ChipInput value={titles} onChange={setTitles} placeholder={cfg.titlesPlaceholder} suggestions={picked.buyer_titles} aria-label="Job titles" />
              </Section>

              {isEdit && sizeSection}


            </div>
          )}
        </div>

        {picked && !loading && (
          <div className="flex items-center justify-between gap-2 border-t bg-muted/30 px-6 py-4">
            {isEdit ? <span /> : <Button variant="link" onClick={() => setPicked(null)} className="gap-1.5 px-0 text-muted-foreground"><ArrowLeft className="h-4 w-4" /> Other suggestions</Button>}
            <div className="flex items-center gap-3">
              {missing.length > 0 && <span className="text-xs text-warning">Missing: {missing.join(", ")}</span>}
              <Button onClick={create} disabled={saving || missing.length > 0} className="gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} {isEdit ? "Save changes" : "Save campaign"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** New strategies use the guided wizard; saved ones open the full editor. */
export function CampaignProfileBuilderDialog(props: Props) {
  const [legacyFor, setLegacyFor] = useState<string | null>(null);
  if (props.editKnowledgeId && legacyFor === props.editKnowledgeId) return <LegacyCampaignProfileBuilderDialog {...props} />;
  return <StrategyWizard {...props} onCannotEdit={() => setLegacyFor(props.editKnowledgeId ?? null)} />;
}
